import {
    MutableRefObject,
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import {
    LendingVaultExpanded,
    ParsedMarketInformationWithLtvInfo,
    useAbfFetches,
    useMarketOracleInfos
} from "@bridgesplit/abf-react";
import { StrategyDuration } from "@bridgesplit/abf-sdk";
import { DraftForm, useDraftForm } from "@bridgesplit/react";
import { ManagerViewTabs } from "app/utils";

import {
    CollateralAllocationForm,
    CollateralForm,
    DetailForm,
    initialCollateralAllocationForm,
    initialCollateralForm,
    initialDetailForm,
    initialPrincipalCapsForm,
    initialRateForm,
    initialSettingsForm,
    PrincipalCapsForm,
    RateForm,
    SettingForm
} from "./type";
import { buildManagerOverviewForms, FormType } from "./common";

type ContextState = {
    tabWithUnsavedEdits: ManagerViewTabs | null;
    resetTokenListFiltersRef: MutableRefObject<(() => void) | null>;
    collateralForm: CollateralForm;
    collateralDraft: DraftForm<CollateralForm>;
    detailForm: DetailForm;
    detailDraft: DraftForm<DetailForm>;
    rateForm: RateForm;
    rateDraft: DraftForm<RateForm>;
    settingsForm: SettingForm;
    settingsDraft: DraftForm<SettingForm>;
    principalCapsForm: PrincipalCapsForm;
    principalDraft: DraftForm<PrincipalCapsForm>;
    collateralAllocationForm: CollateralAllocationForm;
    allocationDraft: DraftForm<CollateralAllocationForm>;
    baselineVault: LendingVaultExpanded | undefined;
    vaultExpanded: LendingVaultExpanded | undefined;
    marketInfo: ParsedMarketInformationWithLtvInfo | undefined;
    reset: () => void;
    resetForm: (formType: FormType) => void;
};

const ManagerViewContext = createContext<ContextState | null>(null);

export function useManagerViewContext() {
    const context = useContext(ManagerViewContext);
    if (!context) {
        throw new Error("useManagerViewContext must be used within a ManagerViewProvider");
    }
    return context;
}

export function ManagerViewProvider({
    children,
    vaultExpanded,
    strategyDurations
}: {
    children: ReactNode;
    vaultExpanded: LendingVaultExpanded | undefined;
    strategyDurations: StrategyDuration[] | undefined;
    isFetching?: boolean;
}) {
    const collateralDraft = useDraftForm<CollateralForm>(initialCollateralForm);
    const detailDraft = useDraftForm<DetailForm>(initialDetailForm);
    const rateDraft = useDraftForm<RateForm>(initialRateForm);
    const settingsDraft = useDraftForm<SettingForm>(initialSettingsForm);
    const principalDraft = useDraftForm<PrincipalCapsForm>(initialPrincipalCapsForm);
    const allocationDraft = useDraftForm<CollateralAllocationForm>(initialCollateralAllocationForm);

    const resetTokenListFiltersRef = useRef<(() => void) | null>(null);
    const {
        resetLendingVaultPublicApi,
        resetLendingVaultMetadataApi,
        resetLendingStrategyApi,
        resetMarketInfoApi,
        resetTimelocksApi
    } = useAbfFetches();

    const [baselineVault, setBaselineVault] = useState<LendingVaultExpanded | undefined>(undefined);
    const hasHydratedRef = useRef(false);
    const pendingServerRehydrateRef = useRef(false);
    const lastHydratedWriteVersionRef = useRef<number | undefined>(undefined);
    const lastHydratedMarketWriteVersionRef = useRef<string | undefined>(undefined);

    const { data: marketData } = useMarketOracleInfos({
        filter: {
            principalMints: vaultExpanded?.vault.principalMint ? [vaultExpanded.vault.principalMint] : [],
            addresses: vaultExpanded?.vaultStrategy?.strategy.marketInformation
                ? [vaultExpanded.vaultStrategy?.strategy.marketInformation]
                : [],
            verified: false
        }
    });

    const marketInfo = useMemo(() => {
        return marketData?.find(
            (m) => m.marketInformation.address === vaultExpanded?.vaultStrategy?.strategy.marketInformation
        );
    }, [marketData, vaultExpanded]);

    const rebaseCollateral = collateralDraft.rebase;
    const rebaseDetail = detailDraft.rebase;
    const rebaseRate = rateDraft.rebase;
    const rebaseSettings = settingsDraft.rebase;
    const rebasePrincipalCaps = principalDraft.rebase;
    const rebaseAllocation = allocationDraft.rebase;
    const latestMarketInfoRef = useRef(marketInfo);
    const latestStrategyDurationsRef = useRef(strategyDurations);

    useEffect(() => {
        latestMarketInfoRef.current = marketInfo;
    }, [marketInfo]);

    useEffect(() => {
        latestStrategyDurationsRef.current = strategyDurations;
    }, [strategyDurations]);

    const hydrateAllFormsFromVault = useCallback(
        (vault: LendingVaultExpanded) => {
            const forms = buildManagerOverviewForms(
                vault,
                latestMarketInfoRef.current,
                latestStrategyDurationsRef.current
            );
            rebaseCollateral(forms.collateralForm);
            rebaseDetail(forms.detailForm);
            rebaseRate(forms.rateForm);
            rebaseSettings(forms.settingsForm);
            rebasePrincipalCaps(forms.principalCapsForm);
            rebaseAllocation(forms.collateralAllocationForm);
            setBaselineVault(vault);
        },
        [rebaseCollateral, rebaseDetail, rebaseRate, rebaseSettings, rebasePrincipalCaps, rebaseAllocation]
    );

    useEffect(() => {
        if (!vaultExpanded) return;
        const writeVersion = vaultExpanded?.vaultStrategy?.strategy?.writeVersion;
        if (writeVersion === undefined) return;
        const previousWriteVersion = lastHydratedWriteVersionRef.current;
        const marketWriteVersion = latestMarketInfoRef.current?.marketInformation?.writeVersion?.toString();
        const previousMarketWriteVersion = lastHydratedMarketWriteVersionRef.current;

        if (!hasHydratedRef.current) {
            hydrateAllFormsFromVault(vaultExpanded);
            lastHydratedWriteVersionRef.current = writeVersion;
            lastHydratedMarketWriteVersionRef.current = marketWriteVersion;
            hasHydratedRef.current = true;
            return;
        }
        if (!pendingServerRehydrateRef.current) return;
        if (!latestMarketInfoRef.current) {
            return;
        }

        const vaultWriteChanged = writeVersion !== previousWriteVersion;
        const marketWriteChanged = marketWriteVersion !== previousMarketWriteVersion;
        if (!vaultWriteChanged && !marketWriteChanged) {
            return;
        }

        hydrateAllFormsFromVault(vaultExpanded);
        lastHydratedWriteVersionRef.current = writeVersion;
        lastHydratedMarketWriteVersionRef.current = marketWriteVersion;
        pendingServerRehydrateRef.current = false;
    }, [vaultExpanded, marketInfo, hydrateAllFormsFromVault]);

    const reset = useCallback(async (): Promise<void> => {
        pendingServerRehydrateRef.current = true;
        await Promise.all([
            resetLendingVaultPublicApi(),
            resetLendingVaultMetadataApi(),
            resetLendingStrategyApi(),
            resetMarketInfoApi(),
            resetTimelocksApi()
        ]);
    }, [
        resetLendingVaultPublicApi,
        resetLendingVaultMetadataApi,
        resetLendingStrategyApi,
        resetMarketInfoApi,
        resetTimelocksApi
    ]);

    const resetForm = useCallback(
        (formType: FormType) => {
            if (
                formType === FormType.COLLATERAL_FORM ||
                formType === FormType.RATE_FORM ||
                formType === FormType.COLLATERAL_ALLOCATION_FORM
            ) {
                resetTokenListFiltersRef.current?.();
            }
            switch (formType) {
                case FormType.COLLATERAL_FORM:
                    collateralDraft.reset();
                    break;
                case FormType.DETAIL_FORM:
                    detailDraft.reset();
                    break;
                case FormType.RATE_FORM:
                    rateDraft.reset();
                    break;
                case FormType.SETTINGS_FORM:
                    settingsDraft.reset();
                    break;
                case FormType.PRINCIPAL_CAPS_FORM:
                    principalDraft.reset();
                    break;
                case FormType.COLLATERAL_ALLOCATION_FORM:
                    allocationDraft.reset();
                    break;
            }
        },
        [collateralDraft, detailDraft, rateDraft, settingsDraft, principalDraft, allocationDraft]
    );

    const tabWithUnsavedEdits = useMemo((): ManagerViewTabs | null => {
        if (!baselineVault) return null;

        if (detailDraft.isDirty) return ManagerViewTabs.Detail;
        if (collateralDraft.isDirtyExcluding("search", "collateralToMarketInfoOptions"))
            return ManagerViewTabs.Collateral;
        if (rateDraft.isDirtyExcluding("search")) return ManagerViewTabs.Rates;
        if (allocationDraft.isDirtyExcluding("search")) return ManagerViewTabs.CollateralAllocation;
        if (settingsDraft.isDirtyExcluding("search", "viewPendingUpdate")) return ManagerViewTabs.Settings;
        if (principalDraft.isDirty) return ManagerViewTabs.PrincipalCaps;

        return null;
    }, [
        baselineVault,
        detailDraft.isDirty,
        collateralDraft,
        rateDraft,
        allocationDraft,
        settingsDraft,
        principalDraft.isDirty
    ]);

    return (
        <ManagerViewContext.Provider
            value={{
                tabWithUnsavedEdits,
                resetTokenListFiltersRef,
                collateralForm: collateralDraft.value,
                collateralDraft,
                detailDraft,
                detailForm: detailDraft.value,
                rateForm: rateDraft.value,
                rateDraft,
                settingsForm: settingsDraft.value,
                settingsDraft,
                principalCapsForm: principalDraft.value,
                principalDraft,
                collateralAllocationForm: allocationDraft.value,
                allocationDraft,
                baselineVault,
                vaultExpanded,
                marketInfo,
                reset,
                resetForm
            }}
        >
            {children}
        </ManagerViewContext.Provider>
    );
}
